import { useRef } from "react";
import { Link } from "react-router";
import { Copy, Download, ExternalLink, Settings, Share2 } from "lucide-react";
import { toast } from "sonner";
import { Popover, PopoverContent, PopoverTrigger } from "@bookdental/ui";
import { trpc } from "@/lib/trpc";
import { usePermissao } from "@/lib/use-permissao";
import { baixarQrDoLink, linkDoCatalogo } from "@/lib/link-do-catalogo";

const acao =
  "inline-flex items-center gap-1.5 rounded-md px-2 py-1 text-sm font-medium text-brand-700 hover:bg-brand-50 dark:text-primary dark:hover:bg-primary/10";

/**
 * "Compartilhar catálogo": o link público (o mesmo do QR da etiqueta) à mão no Catálogo — antes ele
 * só existia no fim de Configurações › Perfil público (pedido do Jun). Diz se o link mostra preço,
 * que é o que importa antes de mandar. "Gerar link novo" fica só na configuração: derruba os QRs
 * já impressos e não pode estar a um clique de quem só queria copiar.
 */
export function CompartilharCatalogo() {
  const config = trpc.catalogoPublico.configuracao.useQuery();
  const copiar = useRef<HTMLButtonElement>(null);
  const podeConfigurar = usePermissao("settings.manage");
  if (!config.data) return null;
  const { codigo, comPreco } = config.data;
  // Desligado e sem como ligar: não há o que compartilhar nem pra onde levar a pessoa.
  if (!codigo && !podeConfigurar) return null;
  const link = codigo ? linkDoCatalogo(codigo) : null;
  const podeEnviar = typeof navigator !== "undefined" && typeof navigator.share === "function";

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="inline-flex h-9 items-center gap-1.5 rounded-md border border-zinc-200 bg-white px-3 text-sm font-semibold text-zinc-700 shadow-sm hover:bg-zinc-50 dark:border-border dark:bg-card dark:text-foreground dark:hover:bg-muted"
        >
          <Share2 className="h-4 w-4" />
          Compartilhar catálogo
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        collisionPadding={16}
        // Foco no Copiar, não no campo: o link aparecia todo selecionado ao abrir, com cara de engano.
        onOpenAutoFocus={(e) => {
          e.preventDefault();
          copiar.current?.focus();
        }}
        className="w-[min(22rem,calc(100vw-2rem))] border border-zinc-200 bg-white p-4 shadow-lg dark:border-border dark:bg-card">
        {link ? (
          <div className="space-y-3">
            <div>
              <p className="text-sm font-semibold text-zinc-900 dark:text-foreground">Link público do catálogo</p>
              <p className="mt-0.5 text-xs text-zinc-500 dark:text-muted-foreground">
                Abre sem login, com os serviços que você oferece. É o mesmo link do QR da etiqueta.
              </p>
            </div>
            <div className="flex items-center gap-2">
              <input readOnly value={link} aria-label="Link do catálogo" onFocus={(e) => e.target.select()} className="h-9 min-w-0 flex-1 rounded-md border border-zinc-200 bg-zinc-50 px-3 text-sm text-zinc-700 dark:border-border dark:bg-muted dark:text-foreground" />
              <button
                ref={copiar}
                type="button"
                onClick={() => navigator.clipboard.writeText(link).then(() => toast.success("Link copiado."))}
                className="inline-flex h-9 shrink-0 items-center gap-1.5 rounded-md bg-brand-600 px-3 text-sm font-semibold text-white shadow-sm hover:bg-brand-700"
              >
                <Copy className="h-4 w-4" />
                Copiar
              </button>
            </div>
            <p className={comPreco ? "text-xs font-medium text-amber-700 dark:text-amber-400" : "text-xs text-zinc-500 dark:text-muted-foreground"}>
              {comPreco ? "Este link mostra os preços." : "Este link não mostra preços."}
            </p>
            <div className="flex flex-wrap gap-1">
              {/* No celular abre o compartilhar do aparelho (WhatsApp, e-mail...). */}
              {podeEnviar && (
                <button type="button" onClick={() => navigator.share({ title: "Catálogo", url: link }).catch(() => {})} className={acao}>
                  <Share2 className="h-4 w-4" />
                  Enviar…
                </button>
              )}
              <a href={link} target="_blank" rel="noreferrer" className={acao}>
                <ExternalLink className="h-4 w-4" />
                Abrir
              </a>
              <button type="button" onClick={() => baixarQrDoLink(link)} className={acao}>
                <Download className="h-4 w-4" />
                Baixar QR
              </button>
            </div>
          </div>
        ) : (
          <div className="space-y-1">
            <p className="text-sm font-semibold text-zinc-900 dark:text-foreground">O link público está desligado</p>
            <p className="text-xs text-zinc-500 dark:text-muted-foreground">
              Ligue para ter um link sem login com os seus serviços — para divulgar e para o QR da etiqueta. Lá você escolhe se ele mostra preços.
            </p>
          </div>
        )}
        {podeConfigurar && (
          <Link
            to="/configuracoes/perfil-publico#catalogo-publico"
            className="mt-3 flex items-center gap-1.5 border-t border-zinc-100 pt-3 text-sm font-semibold text-brand-700 hover:underline dark:border-border dark:text-primary"
          >
            <Settings className="h-4 w-4" />
            {link ? "Configurar catálogo público" : "Ligar o link público"}
          </Link>
        )}
      </PopoverContent>
    </Popover>
  );
}
